import React, { useEffect, useState } from "react"
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import moment from "moment"
import API from "@/common/API"

interface BackfillStopResult {
  id: string
  placeId: string
  placeName: string
  date: string
  assignedName?: string
  assignerId?: string
  visitId: string
  completedAt: string
  notified: boolean
}

interface BackfillResponse {
  totalPendingChecked: number
  completedStops: number
  notificationsSent: number
  dryRun: boolean
  stops: BackfillStopResult[]
}

interface OrganizationOption {
  id: string
  name: string
}

interface RouteBackfillModalProps {
  show: boolean
  onHide: () => void
  defaultOrgId?: string
}

const RouteBackfillModal: React.FC<RouteBackfillModalProps> = ({ show, onHide, defaultOrgId }) => {
  const { t } = useTranslation("global")
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState<string>(defaultOrgId || "")
  const [fromDate, setFromDate] = useState<string>("")
  const [toDate, setToDate] = useState<string>("")
  const [notifyAssigners, setNotifyAssigners] = useState<boolean>(true)
  const [isRunning, setIsRunning] = useState<boolean>(false)
  const [result, setResult] = useState<BackfillResponse | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (defaultOrgId !== undefined) {
      setSelectedOrgId(defaultOrgId)
    }
  }, [defaultOrgId])

  useEffect(() => {
    if (show) {
      setErrorMessage(null)
      API.getAction("Organization/AdminList", "Page=0&Quantity=200")
        .then(res => {
          if (res.data?.status === "ok") {
            setOrganizations((res.data.response ?? []).map((item: any) => ({
              id: item.id,
              name: item.name,
            })))
          }
        })
        .catch(err => {
          console.error("Error loading organizations for backfill:", err)
        })
    }
  }, [show])

  const handleExecute = async (dryRun: boolean) => {
    try {
      setIsRunning(true)
      setErrorMessage(null)

      const payload: any = {
        OrganizationId: selectedOrgId || undefined,
        NotifyAssigners: notifyAssigners,
        DryRun: dryRun,
      }
      if (fromDate) payload.FromDate = fromDate
      if (toDate) payload.ToDate = toDate

      const res = await API.postAction("WeeklyRoutes/BackfillCompletedVisits", payload)

      if (res.data?.status === "ok") {
        setResult(res.data.response)
      } else {
        setErrorMessage(res.data?.response || t("Ocurrió un error al ejecutar el backfill."))
      }
    } catch (ex: any) {
      console.error("Error running route backfill:", ex)
      const msg = ex?.response?.data?.response || ex?.message || t("Error en la conexión con el servidor.")
      setErrorMessage(msg)
    } finally {
      setIsRunning(false)
    }
  }

  const handleClose = () => {
    if (isRunning) return
    setResult(null)
    setErrorMessage(null)
    onHide()
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop={isRunning ? "static" : true} centered>
      <Modal.Header closeButton={!isRunning} className="bg-light">
        <Modal.Title className="h6 mb-0 d-flex align-items-center gap-2">
          <i className="fas fa-route text-primary fs-5" />
          <span className="fw-bold">{t("Movilizar Rutas y Notificar (Backfill)")}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="py-3">
        <p className="text-muted small mb-3">
          {t("Coteja automáticamente las paradas de ruta asignadas con las visitas registradas por los agentes en campo. Las paradas coincidentes se marcarán como completadas y se enviará una notificación a la persona que las asignó.")}
        </p>

        {errorMessage && (
          <Alert variant="danger" className="py-2 small mb-3" dismissible onClose={() => setErrorMessage(null)}>
            <i className="fas fa-triangle-exclamation me-1" />
            {errorMessage}
          </Alert>
        )}

        <Card className="border bg-light mb-3">
          <Card.Body className="py-2 px-3">
            <Row className="g-3 align-items-end">
              <Col xs={12} md={6}>
                <Form.Label className="small fw-semibold mb-1">
                  <i className="fas fa-building text-secondary me-1" />
                  {t("Organización")}
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={selectedOrgId}
                  disabled={isRunning}
                  onChange={e => setSelectedOrgId(e.target.value)}
                >
                  <option value="">{t("Todas las organizaciones (Superadmin)")}</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </Form.Select>
              </Col>

              <Col xs={6} md={3}>
                <Form.Label className="small fw-semibold mb-1">{t("Desde (Opcional)")}</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={fromDate}
                  disabled={isRunning}
                  onChange={e => setFromDate(e.target.value)}
                />
              </Col>

              <Col xs={6} md={3}>
                <Form.Label className="small fw-semibold mb-1">{t("Hasta (Opcional)")}</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={toDate}
                  disabled={isRunning}
                  onChange={e => setToDate(e.target.value)}
                />
              </Col>

              <Col xs={12}>
                <div className="d-flex align-items-center gap-3 pt-1">
                  <Form.Check
                    type="switch"
                    id="notify-assigners-switch"
                    label={t("Notificar a supervisores / asignadores por app y push")}
                    checked={notifyAssigners}
                    disabled={isRunning}
                    onChange={e => setNotifyAssigners(e.target.checked)}
                    className="small fw-semibold"
                  />
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Results view */}
        {result && (
          <div className="mt-3">
            <Alert variant={result.dryRun ? "info" : "success"} className="mb-3 py-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="fw-bold">
                  <i className={`fas ${result.dryRun ? "fa-circle-info" : "fa-circle-check"} me-2`} />
                  {result.dryRun ? t("Simulación Finalizada (Dry Run - Sin Cambios)") : t("Backfill Ejecutado Exitosamente")}
                </span>
                <Badge bg={result.dryRun ? "secondary" : "success"}>
                  {result.dryRun ? "Simulación" : "Aplicado"}
                </Badge>
              </div>
              <div className="small">
                <strong>{result.totalPendingChecked}</strong> paradas pendientes analizadas ·{" "}
                <strong className="text-primary">{result.completedStops}</strong> {result.dryRun ? "coinciden para completar" : "completadas"} ·{" "}
                <strong>{result.notificationsSent}</strong> notificaciones {result.dryRun ? "a enviar" : "enviadas"}
              </div>
            </Alert>

            {result.stops && result.stops.length > 0 ? (
              <div className="border rounded overflow-hidden" style={{ maxHeight: 260, overflowY: "auto" }}>
                <Table size="sm" hover className="mb-0 small align-middle">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>{t("Cliente / Parada")}</th>
                      <th>{t("Fecha Programada")}</th>
                      <th>{t("Agente Asignado")}</th>
                      <th>{t("Fecha Visita")}</th>
                      <th className="text-center">{t("Notificación")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.stops.map((stop, idx) => (
                      <tr key={stop.id || idx}>
                        <td className="fw-semibold text-truncate" style={{ maxWidth: 180 }} title={stop.placeName}>
                          {stop.placeName}
                        </td>
                        <td>{stop.date ? moment(stop.date).format("DD/MM/YYYY") : "—"}</td>
                        <td>{stop.assignedName || "—"}</td>
                        <td>
                          <Badge bg="light" text="dark" className="border">
                            {stop.completedAt ? moment(stop.completedAt).format("DD/MM/YYYY HH:mm") : "—"}
                          </Badge>
                        </td>
                        <td className="text-center">
                          {stop.notified ? (
                            <Badge bg="success"><i className="fas fa-check me-1" />Enviada</Badge>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center text-muted py-3 small border rounded bg-light">
                {t("No se encontraron paradas pendientes adicionales con visitas registradas en el rango seleccionado.")}
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-light d-flex justify-content-between">
        <Button variant="secondary" size="sm" onClick={handleClose} disabled={isRunning}>
          {result ? t("Cerrar") : t("Cancelar")}
        </Button>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            disabled={isRunning}
            onClick={() => handleExecute(true)}
          >
            {isRunning ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                {t("Procesando...")}
              </>
            ) : (
              <>
                <i className="fas fa-flask me-1" />
                {t("Simular (Dry Run)")}
              </>
            )}
          </Button>

          <Button
            variant="primary"
            size="sm"
            disabled={isRunning}
            onClick={() => handleExecute(false)}
          >
            {isRunning ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                {t("Aplicando...")}
              </>
            ) : (
              <>
                <i className="fas fa-play me-1" />
                {t("Ejecutar y Movilizar")}
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default RouteBackfillModal
